import { Alert,Button,Flex,Select,Space,StatusText,Typography } from "../../../../../../../shared/frontend/ui";
import type { ReactNode } from "react";
import type { AccessMediumAssignmentReader } from "../../../features/accessDevices/useAccessMediumAssignmentReaders";
import { spacing } from "../../../app/design-system";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import { ThemedModal } from "../../../components/ThemedModal";

type AccessMediumScanConflict = {
  mediumId: string;
  accessMediumId: string;
  personId: string;
  personName: string;
};

type Props = {
  open: boolean;
  saving: boolean;
  readerScope?: {
    studioName?: string;
    hasStudio: boolean;
    readers: AccessMediumAssignmentReader[];
    loading: boolean;
    failed: boolean;
    onRetry: () => void;
  };
  readerOptions?: { value: string; label: string }[];
  selectedReaderDeviceHardwareId?: string | null;
  showReaderSelection?: boolean;
  scanHint: string | null;
  scanConflict: AccessMediumScanConflict | null;
  text: I18nContextValue["text"];
  description?: ReactNode;
  onScanAgain?: () => void;
  onReaderChange?: (value: string | null) => void;
  onClose: () => void;
  onReassign: (mediumId: string) => void | Promise<void>;
};

export function AccessMediumAssignModal({
  open,
  saving,
  readerScope,
  readerOptions = [],
  selectedReaderDeviceHardwareId = null,
  showReaderSelection = true,
  scanHint,
  scanConflict,
  text,
  description,
  onScanAgain,
  onReaderChange,
  onClose,
  onReassign
}: Props) {
  const needsReaderSelection = showReaderSelection && readerOptions.length > 1 && !selectedReaderDeviceHardwareId;
  const hasNoAssignmentReader = !readerScope && readerOptions.length === 0;

  return (
    <ThemedModal
      title={text("Assign access medium")}
      open={open}
      destroyOnClose
      width={"min(720px, 96vw)"}
      onCancel={onClose}
      closable
      maskClosable={false}
      keyboard
      autoFocusFirstField={false}
      contentLoading={readerScope?.loading}
      footer={
        <Space>
          <Button onClick={onClose}>
            {text("Cancel")}
          </Button>
        </Space>
      }
    >
      {readerScope ? (
        <Flex vertical gap={spacing.md} style={{ marginBottom: spacing.md }}>
          {readerScope.failed ? (
            <Alert type="error" showIcon message={text("Assignment readers could not be loaded.")}
              action={<Button onClick={readerScope.onRetry}>{text("Retry")}</Button>} />
          ) : !readerScope.hasStudio ? (
            <Alert type="info" showIcon message={text("Set the current location in Check-in before assigning an access medium.")} />
          ) : readerScope.loading ? null : readerScope.readers.length === 0 ? (
            <Alert type="warning" showIcon message={text("No assignment reader configured at this location.")}
              description={text("Enable assignment for at least one m1connect reader in reader device settings.")} />
          ) : (
            <>
              <Typography.Text strong>{text("Monitored readers")} · {readerScope.studioName}</Typography.Text>
              {readerScope.readers.map(reader => (
                <Flex key={reader.id} justify="space-between" align="center" gap={spacing.sm} wrap>
                  <Typography.Text>{reader.deviceName} · {reader.hardwareName}</Typography.Text>
                  <StatusText color={reader.online ? "success" : "error"}>
                    {reader.online ? text("Reader device status online") : text("Reader device status offline")}
                  </StatusText>
                </Flex>
              ))}
              {readerScope.readers.every(reader => !reader.online) ? (
                <Alert type="warning" showIcon message={text("All configured assignment readers are offline.")} />
              ) : (
                <Typography.Text type="secondary">{text("Present the chip to one of these readers.")}</Typography.Text>
              )}
            </>
          )}
        </Flex>
      ) : (
        <Typography.Paragraph type="secondary" style={{ marginBottom: spacing.md }}>
          {description ?? text("Present the chip to a m1connect reader. The chip will be assigned to the current person immediately.")}
        </Typography.Paragraph>
      )}

      {hasNoAssignmentReader ? (
        <Alert
          type="warning"
          showIcon
          message={text("No assignment reader configured")}
          description={text("Enable assignment for at least one m1connect reader in reader device settings.")}
          style={{ marginBottom: spacing.md }}
        />
      ) : showReaderSelection && readerOptions.length > 1 ? (
        <div style={{ marginBottom: spacing.md }}>
          <Typography.Text type="secondary" style={{ display: "block", marginBottom: spacing.xs }}>
            {text("Assignment reader")}
          </Typography.Text>
          <Select
            allowClear
            style={{ width: "100%" }}
            value={selectedReaderDeviceHardwareId || undefined}
            options={readerOptions}
            placeholder={text("Select assignment reader")}
            disabled={saving}
            onChange={(value) => onReaderChange?.(value || null)}
          />
        </div>
      ) : null}

      {needsReaderSelection ? (
        <Typography.Text type="secondary">
          {text("Select an assignment reader to start scanning.")}
        </Typography.Text>
      ) : saving ? (
        <Typography.Text type="secondary">
          {text("Waiting for chip…")}
        </Typography.Text>
      ) : null}

      {scanHint ? (
        <div style={{ marginTop: spacing.md }}>
          <Space wrap>
            <Typography.Text type="secondary">{scanHint}</Typography.Text>
            {onScanAgain && <Button disabled={saving || readerScope?.failed || !readerScope?.hasStudio || readerScope.readers.length === 0} onClick={onScanAgain}>{text("Retry")}</Button>}
          </Space>
        </div>
      ) : null}

      {scanConflict ? (
        <div style={{ marginTop: spacing.lg }}>
          <Typography.Paragraph style={{ marginBottom: spacing.sm }}>
            {text("Chip already assigned to").replace("{0}", scanConflict.personName || scanConflict.personId || "")}
          </Typography.Paragraph>

          <Space wrap>
            <Button
              danger
              type="primary"
              disabled={saving}
              onClick={() => onReassign(scanConflict.mediumId)}
            >
              {text("Overwrite")}
            </Button>
            <Button onClick={onClose}>
              {text("Cancel")}
            </Button>
          </Space>
        </div>
      ) : null}
    </ThemedModal>
  );
}
